import React, { FC, useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { Stripe, loadStripe } from "@stripe/stripe-js";
import { styled } from "@material-ui/styles";

import AdvertiserPaymentMethodModal from "../AdvertisersTable/AdvertiserPaymentMethodModal";
import creditCardIcon from "../../../../assets/icons/CreditCardIcon.svg";
import { AdvertiserType } from "../../../../types/advertiserTypes";
import { toTitleCase } from "../../../../helpers/toTitleCase";

type Props = {
  advertiser: AdvertiserType;
  fetchAndSetAdvertiserDetailData: () => void;
};

const Card = styled("div")({
  background: "#FFFFFF",
  border: "1px solid #E5E7EB",
  borderRadius: 10,
  padding: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
});

const Left = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 16,
});

const CardIcon = styled("img")({
  width: 40,
  height: 40,
  flexShrink: 0,
});

const StatusText = styled("div")({
  fontSize: 13,
  fontWeight: 600,
  color: "#10B981",
});

const NoCardText = styled("div")({
  fontSize: 13,
  fontWeight: 600,
  color: "#6B7280",
});

const Label = styled("div")({
  fontSize: 13,
  color: "#6B7280",
  marginTop: 2,
});

const FriendlyName = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const FriendlyLabel = styled("div")({
  fontSize: 12,
  color: "#6B7280",
});

const FriendlyValue = styled("div")({
  fontSize: 13,
  color: "#111827",
});

const Buttons = styled("div")({
  display: "flex",
  gap: 8,
});

const OutlineBtn = styled("button")({
  background: "#FFFFFF",
  border: "1px solid #D1D5DB",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  color: "#374151",
  padding: "8px 14px",
  cursor: "pointer",
  "&:hover": { background: "#F9FAFB" },
});

const BillingCard: FC<Props> = ({ advertiser, fetchAndSetAdvertiserDetailData }) => {
  const { paymentMethod, advertiser_card_name, brandgenAdvertiserId, name } = advertiser;

  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!).then(setStripeInstance).catch(console.log);
  }, []);

  return (
    <Card>
      <Left>
        <CardIcon src={creditCardIcon} alt="credit card" />
        <div>
          {paymentMethod ? (
            <StatusText>
              * * * * {paymentMethod.card.last4} ({toTitleCase(paymentMethod.card.brand)})
            </StatusText>
          ) : (
            <NoCardText>No Card</NoCardText>
          )}
          <Label>Current Selected Card</Label>
        </div>

        {advertiser_card_name && (
          <FriendlyName>
            <FriendlyLabel>Card Friendly Name:</FriendlyLabel>
            <FriendlyValue>{advertiser_card_name}</FriendlyValue>
          </FriendlyName>
        )}
      </Left>

      <Buttons>
        <OutlineBtn onClick={() => setIsModalOpen(true)}>{paymentMethod ? "Edit" : "Add"} Payment Method</OutlineBtn>
      </Buttons>

      {stripeInstance && (
        <Elements stripe={stripeInstance}>
          <AdvertiserPaymentMethodModal
            isOpen={isModalOpen}
            advertiserId={brandgenAdvertiserId}
            advertiserName={name}
            hasExistingCard={!!paymentMethod}
            onClose={() => setIsModalOpen(false)}
            onSuccess={fetchAndSetAdvertiserDetailData}
          />
        </Elements>
      )}
    </Card>
  );
};

export default BillingCard;
